/**
 * Construction Deadlines — licenses with an open FCC construction / build-out
 * obligation (BF.dat) that is approaching or overdue, linked to a CRM client/lead.
 * Staff acknowledge an alert once handled; acknowledged alerts drop out of the
 * default view. Built on the construction-deadline tracking plan (Phase A3).
 */
import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  getConstructionDeadlines,
  acknowledgeConstructionDeadline,
  bulkAcknowledgeConstructionDeadlines,
  logConstructionContact,
} from '../../services/licenses'
import { formatLocalDate } from '../../utils/formatDateTime'
import { Pagination } from '../../components/Pagination'
import FilterBar from '../../components/FilterBar'
import Modal, { ModalFooter } from '../../components/Modal'
import { SortableHeader } from '../../components/SortableHeader'
import { useTableSort } from '../../hooks/useTableSort'
import { RowCheckbox, SelectAllCheckbox, BulkActionBar } from '../../components/BulkSelect'
import { useRowSelection } from '../../hooks/useRowSelection'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import {
  isPageNumber, isStateCode, listStateKey, oneOf, textUpTo, usePageResetOnChange, usePageSize, useSessionState,
} from '../../hooks/useSessionState'
import { useLicenseFacets, facetFilterOptions } from '../../utils/facetOptions'
import SendMessageModal from '../../components/SendMessageModal'
import AddLeadModal from '../../components/AddLeadModal'
import PageHeading from '../../components/PageHeading'
import {
  CONSTRUCTION_ACTION_HELP as ACTION_HELP, CONSTRUCTION_DEADLINES_HELP as HELP,
} from '../../content/pageHelp'
import IconAction, { IconActions } from '../../components/IconAction'
import {
  CheckCircleIcon, DocumentTextIcon, MailIcon, PhoneIcon, UndoIcon, UserAddIcon,
} from '../../components/Icons'
import type { ConstructionDeadlineItem, ConstructionFilters, ContactFilter, ContactMethod } from '../../types'

const WINDOW_OPTIONS = [30, 60, 90, 120, 180, 365]

// How far into the past to surface overdue obligations. Default ~12 years hides the
// long tail of ancient ULS records whose construction was never recorded. 0 = no limit.
const OVERDUE_WINDOW_OPTIONS: { label: string; days: number }[] = [
  { label: '1 year', days: 365 },
  { label: '2 years', days: 730 },
  { label: '5 years', days: 1825 },
  { label: '10 years', days: 3650 },
  { label: '12 years', days: 4380 },
  { label: 'No limit', days: 0 },
]

const CONTACT_FILTERS: { value: ContactFilter; label: string }[] = [
  { value: 'all', label: 'Contacted or not' },
  { value: 'needs_contact', label: 'Needs first contact' },
  { value: 'contacted', label: 'Already contacted' },
]

const CONTACT_METHODS: { value: ContactMethod; label: string; past: string }[] = [
  { value: 'call', label: 'Phone call', past: 'Called' },
  { value: 'in_person', label: 'In person', past: 'Met' },
  { value: 'email', label: 'E-mail', past: 'E-mailed' },
  { value: 'letter', label: 'Letter', past: 'Wrote' },
  { value: 'other', label: 'Other', past: 'Contacted' },
]

/**
 * The Contact cell. "Needs first contact" is the one meant to stand out (P7);
 * a contacted row says how and when, quietly.
 */
function ContactCell({ item }: { item: ConstructionDeadlineItem }) {
  if (item.contact_state === 'needs_contact') {
    return (
      <span className="inline-block rounded px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800">
        Needs first contact
      </span>
    )
  }
  if (item.contact_state === 'handled') return <span className="text-gray-400">—</span>
  if (item.contact_method === 'lead') {
    return <span className="text-xs text-gray-500">Lead being worked</span>
  }
  const verb = CONTACT_METHODS.find(m => m.value === item.contact_method)?.past ?? 'Contacted'
  return (
    <span className="text-xs text-gray-500">
      {verb} {item.contacted_at ? formatLocalDate(item.contacted_at) : ''}
      {item.contacted_by_name ? ` · ${item.contacted_by_name}` : ''}
    </span>
  )
}

// days_remaining is not offered: it orders rows exactly as the deadline does, and
// "When" now sits under the date rather than in a column of its own.
type SortKey = 'call_sign' | 'entity_name' | 'deadline' | 'open_count'
const SORT_KEYS: readonly SortKey[] = ['call_sign', 'entity_name', 'deadline', 'open_count']

// Filters, page and sort are kept for the tab (useSessionState), so following a
// call sign or client link and coming Back finds the list as it was left.
const KEY = listStateKey('constructionDeadlines')
const isWindow = oneOf(WINDOW_OPTIONS)
const isOverdueWindow = oneOf(OVERDUE_WINDOW_OPTIONS.map(o => o.days))

function DaysBadge({ item }: { item: ConstructionDeadlineItem }) {
  if (item.status === 'overdue') {
    const overdueBy = Math.abs(item.days_remaining)
    return (
      <span className="inline-block rounded px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-800">
        {overdueBy} day{overdueBy !== 1 ? 's' : ''} overdue
      </span>
    )
  }
  return (
    <span className="inline-block rounded px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800">
      in {item.days_remaining} day{item.days_remaining !== 1 ? 's' : ''}
    </span>
  )
}

export default function ConstructionDeadlinesPage() {
  const [items, setItems] = useState<ConstructionDeadlineItem[]>([])
  const [approaching, setApproaching] = useState(0)
  const [overdue, setOverdue] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // A message about a deadline goes to the CRM record when the row has one —
  // so it lands in that client's history and resolves against Do Not Contact —
  // and to the license itself when it does not, where the sender types the
  // address because the FCC record is all we have.
  const [messageTarget, setMessageTarget] = useState<ConstructionDeadlineItem | null>(null)
  const [busyUsi, setBusyUsi] = useState<number | null>(null)
  // A license with a deadline and no CRM record is a prospect; this tracks it.
  const [leadTarget, setLeadTarget] = useState<ConstructionDeadlineItem | null>(null)
  // "Log contact" — a call or a visit, which nothing else records for a bare license.
  const [contactTarget, setContactTarget] = useState<ConstructionDeadlineItem | null>(null)
  const [contactMethod, setContactMethod] = useState<ContactMethod>('call')
  const [needsContact, setNeedsContact] = useState(0)

  // Filters
  const [withinDays, setWithinDays] = useSessionState(KEY('withinDays'), 120, isWindow)
  const [includeOverdue, setIncludeOverdue] = useSessionState(KEY('includeOverdue'), true)
  const [overdueWithinDays, setOverdueWithinDays] =
    useSessionState(KEY('overdueWithinDays'), 4380, isOverdueWindow)
  const [linkedOnly, setLinkedOnly] = useSessionState(KEY('linkedOnly'), true)
  const [includeAcknowledged, setIncludeAcknowledged] =
    useSessionState(KEY('includeAcknowledged'), false)
  const [state, setState] = useSessionState(KEY('state'), '', isStateCode)
  const [search, setSearch] = useSessionState(KEY('search'), '', textUpTo(200))
  const [contact, setContact] = useSessionState<ContactFilter>(
    KEY('contact'), 'all', oneOf(CONTACT_FILTERS.map(f => f.value)),
  )
  // Debounced: hydrating this list is expensive, so it must not run per keystroke.
  const debouncedSearch = useDebouncedValue(search)

  // Pagination + sort
  const [page, setPage] = useSessionState(KEY('page'), 1, isPageNumber)
  const [pageSize, setPageSize] = usePageSize('constructionDeadlines', 50)
  const { sortKey, sortOrder, handleSort } = useTableSort<SortKey>(
    'deadline', 'asc',
    { descFirst: [], onSortChange: () => setPage(1), persist: { key: KEY('sort'), keys: SORT_KEYS } },
  )

  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState<null | { acknowledged: boolean }>(null)
  const facets = useLicenseFacets()

  /**
   * The filter set in exactly the shape the API takes, so the same object drives the
   * list request and a filter-mode bulk request. Two shapes would be two chances to
   * disagree about what the screen is showing.
   */
  const filters = useMemo<ConstructionFilters>(
    () => ({
      within_days: withinDays,
      include_overdue: includeOverdue,
      // days===0 means "no limit": omit the param so the API applies no lower bound.
      ...(includeOverdue && overdueWithinDays > 0 ? { overdue_within_days: overdueWithinDays } : {}),
      linked_only: linkedOnly,
      include_acknowledged: includeAcknowledged,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(state ? { state } : {}),
      ...(contact !== 'all' ? { contact } : {}),
    }),
    [withinDays, includeOverdue, overdueWithinDays, linkedOnly, includeAcknowledged,
     debouncedSearch, state, contact],
  )

  const selection = useRowSelection<number>(
    items.map((i) => i.fcc_unique_system_identifier),
    JSON.stringify([filters, page, sortKey, sortOrder]),
  )

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await getConstructionDeadlines({
        ...filters,
        page,
        page_size: pageSize,
        sort: sortKey,
        order: sortOrder,
      })
      setItems(resp.items)
      setTotal(resp.total)
      setApproaching(resp.approaching)
      setOverdue(resp.overdue)
      setNeedsContact(resp.needs_contact)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load construction deadlines')
    } finally {
      setLoading(false)
    }
  }, [filters, page, pageSize, sortKey, sortOrder])

  useEffect(() => { load() }, [load])

  // Back to page 1 when filters or sort change. One effect over the whole view rather
  // than the applyFilter(set) wrapper some pages use: with this many filters it cannot
  // forget one, whereas a new filter wired without the wrapper would silently keep
  // you on page 7 of a different list.
  usePageResetOnChange(JSON.stringify([filters, sortKey, sortOrder]), setPage)

  async function handleAck(item: ConstructionDeadlineItem) {
    setBusyUsi(item.fcc_unique_system_identifier)
    try {
      await acknowledgeConstructionDeadline(item.fcc_unique_system_identifier, !item.acknowledged)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update alert')
    } finally {
      setBusyUsi(null)
    }
  }

  async function saveContact(item: ConstructionDeadlineItem, contacted: boolean) {
    setBusyUsi(item.fcc_unique_system_identifier)
    try {
      await logConstructionContact(item.fcc_unique_system_identifier, contacted, contactMethod)
      setContactTarget(null)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save the contact')
    } finally {
      setBusyUsi(null)
    }
  }

  async function runBulk(acknowledged: boolean) {
    setBusy(true)
    setError(null)
    try {
      await bulkAcknowledgeConstructionDeadlines(
        selection.allMatching
          ? { mode: 'filter', acknowledged, filters }
          : { mode: 'ids', acknowledged, usis: [...selection.selected] },
      )
      selection.clear()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update alerts')
    } finally {
      setBusy(false)
      setConfirming(null)
    }
  }

  /** Selected rows act immediately; "all matching" reaches rows off screen, so it asks. */
  function requestBulk(acknowledged: boolean) {
    if (selection.allMatching) setConfirming({ acknowledged })
    else runBulk(acknowledged)
  }

  function linkedCell(item: ConstructionDeadlineItem) {
    if (item.client_id) {
      return <Link to={`/crm/clients/${item.client_id}`} className="tap text-primary-600 hover:underline">Client</Link>
    }
    if (item.lead_id) {
      return <Link to={`/crm/leads/${item.lead_id}`} className="tap text-primary-600 hover:underline">Lead</Link>
    }
    return <span className="text-gray-400">—</span>
  }

  const totalPages = Math.ceil(total / pageSize)

  return (
    <div className="space-y-4">
      <PageHeading title="Construction Deadlines" help={HELP} />

      {/* Summary + filters. Search first, then the dropdowns, then this page's own
          checkboxes — the same order as every other list page. */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4 text-sm mb-3">
          {needsContact > 0 && (
            <span className="font-semibold text-amber-800">{needsContact} need first contact</span>
          )}
          <span className="font-semibold text-red-700">{overdue} overdue</span>
          <span className="font-semibold text-amber-700">{approaching} approaching</span>
          {total > 0 && <span className="text-gray-500">{total} total</span>}
        </div>

        <FilterBar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search call sign or entity…"
          searchLabel="Search construction deadlines"
          selects={[
            {
              id: 'within',
              label: 'Show deadlines due within',
              value: String(withinDays),
              onChange: (v) => setWithinDays(Number(v)),
              neutralValue: '120',
              options: WINDOW_OPTIONS.map(d => ({ value: String(d), label: `Due within ${d} days` })),
            },
            {
              id: 'construction-contact',
              label: 'Filter by first contact',
              value: contact,
              onChange: (v) => setContact(v as ContactFilter),
              neutralValue: 'all',
              options: CONTACT_FILTERS,
            },
            {
              id: 'construction-state',
              label: 'Filter by licensee state',
              value: state,
              onChange: setState,
              options: facetFilterOptions('All states', facets?.states, state),
            },
          ]}
          extraActiveCount={
            (includeOverdue ? 0 : 1) +
            (linkedOnly ? 0 : 1) +
            (includeAcknowledged ? 1 : 0) +
            // Counted because Clear resets it. Omitting it made the link read
            // "Clear 2 filters" while clearing three.
            (overdueWithinDays === 4380 ? 0 : 1)
          }
          onClearExtras={() => {
            setIncludeOverdue(true)
            setLinkedOnly(true)
            setIncludeAcknowledged(false)
            setOverdueWithinDays(4380)
          }}
        >
          <label className="flex items-center gap-1.5 text-sm text-gray-700">
            <input type="checkbox" checked={includeOverdue} onChange={e => setIncludeOverdue(e.target.checked)} />
            Include overdue
          </label>
          {includeOverdue && (
            <label className="flex items-center gap-1.5 text-sm text-gray-700">
              <span className="text-gray-600">back to</span>
              <select
                className="filter-control"
                aria-label="How far back to show overdue obligations"
                value={overdueWithinDays}
                onChange={e => setOverdueWithinDays(Number(e.target.value))}
              >
                {OVERDUE_WINDOW_OPTIONS.map(o => (
                  <option key={o.days} value={o.days}>{o.label}</option>
                ))}
              </select>
            </label>
          )}
          <label className="flex items-center gap-1.5 text-sm text-gray-700">
            <input type="checkbox" checked={linkedOnly} onChange={e => setLinkedOnly(e.target.checked)} />
            Linked to a client/lead only
          </label>
          <label className="flex items-center gap-1.5 text-sm text-gray-700">
            <input type="checkbox" checked={includeAcknowledged} onChange={e => setIncludeAcknowledged(e.target.checked)} />
            Show acknowledged
          </label>
        </FilterBar>
      </div>

      {error && <div role="alert" className="card border-red-200 bg-red-50 text-sm text-red-700">{error}</div>}

      <BulkActionBar
        count={selection.count}
        total={total}
        pageSize={pageSize}
        allMatching={selection.allMatching}
        onSelectAllMatching={selection.selectAllMatching}
        onClear={selection.clear}
      >
        <button
          type="button"
          onClick={() => requestBulk(true)}
          disabled={busy}
          className="btn-sm btn-primary disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Acknowledge'}
        </button>
        {/* Only offered when acknowledged rows can actually be on screen — with
            "Show acknowledged" off, everything visible is unacknowledged. */}
        {includeAcknowledged && (
          <button
            type="button"
            onClick={() => requestBulk(false)}
            disabled={busy}
            className="btn-sm btn-secondary disabled:opacity-50"
          >
            Un-acknowledge
          </button>
        )}
      </BulkActionBar>

      <div className="card overflow-x-auto">
        {loading ? (
          <p className="text-sm text-gray-500 py-6 text-center">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">
            {debouncedSearch
              ? `No open construction obligations match “${debouncedSearch}”.`
              : 'No open construction obligations in this window. 🎉'}
          </p>
        ) : (
          <>
            <div className="table-wrap">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                    <th className="py-2 pr-3 w-px">
                      <SelectAllCheckbox state={selection.pageState} onToggle={selection.togglePage} />
                    </th>
                    <SortableHeader sortKey="call_sign" currentSortKey={sortKey} currentSortOrder={sortOrder} onSort={handleSort} className="py-2 pr-3">
                      Call Sign
                    </SortableHeader>
                    <SortableHeader sortKey="entity_name" currentSortKey={sortKey} currentSortOrder={sortOrder} onSort={handleSort} className="py-2 pr-3">
                      Licensee
                    </SortableHeader>
                    <SortableHeader sortKey="deadline" currentSortKey={sortKey} currentSortOrder={sortOrder} onSort={handleSort} className="py-2 pr-3">
                      Deadline
                    </SortableHeader>
                    <SortableHeader sortKey="open_count" currentSortKey={sortKey} currentSortOrder={sortOrder} onSort={handleSort} className="py-2 pr-3 hidden sm:table-cell">
                      Freqs
                    </SortableHeader>
                    <th className="py-2 pr-3 hidden md:table-cell">Linked</th>
                    <th className="py-2 pr-3">Contact</th>
                    <th className="py-2 pr-3 hidden md:table-cell">Assigned</th>
                    <th className="py-2 pr-1 text-right col-actions-sticky bg-white">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.fcc_unique_system_identifier}
                        className={`group border-b last:border-0 bg-white hover:bg-gray-50 ${item.acknowledged ? 'opacity-60' : item.contact_state === 'contacted' ? 'opacity-80' : ''}`}>
                      <td className="py-2 pr-3 w-px">
                        <RowCheckbox
                          checked={selection.isSelected(item.fcc_unique_system_identifier)}
                          onChange={() => selection.toggle(item.fcc_unique_system_identifier)}
                          label={item.call_sign || `license ${item.fcc_unique_system_identifier}`}
                        />
                      </td>
                      <td className="py-2 pr-3 font-medium">
                        {item.call_sign ? (
                          <Link to={`/licenses/${item.call_sign}`} className="tap text-primary-600 hover:underline">
                            {item.call_sign}
                          </Link>
                        ) : '—'}
                      </td>
                      <td className="py-2 pr-3 max-w-[14rem] truncate" title={item.entity_name || undefined}>
                        {item.entity_name || '—'}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        <div>{formatLocalDate(item.earliest_deadline)}</div>
                        <DaysBadge item={item} />
                      </td>
                      <td className="py-2 pr-3 hidden sm:table-cell">{item.open_count}</td>
                      <td className="py-2 pr-3 hidden md:table-cell">{linkedCell(item)}</td>
                      <td className="py-2 pr-3 max-w-[11rem]"><ContactCell item={item} /></td>
                      <td className="py-2 pr-3 hidden md:table-cell">{item.assigned_to_name || <span className="text-gray-400">—</span>}</td>
                      <td className="py-2 pr-1 col-actions-sticky group-hover:bg-gray-50">
                        <IconActions>
                          {item.call_sign && (
                            <IconAction
                              label="Prepare 601"
                              description={ACTION_HELP.prepare}
                              icon={<DocumentTextIcon />}
                              href={`/licenses/${item.call_sign}/construction-worksheet`}
                            />
                          )}
                          {!item.client_id && !item.lead_id && (
                            <IconAction
                              label="Add as lead"
                              description={ACTION_HELP.lead}
                              icon={<UserAddIcon />}
                              onClick={() => setLeadTarget(item)}
                            />
                          )}
                          <IconAction
                            label="Send message"
                            description={ACTION_HELP.message}
                            icon={<MailIcon />}
                            tone="primary"
                            onClick={() => setMessageTarget(item)}
                          />
                          {item.contact_state !== 'handled' && (
                            <IconAction
                              label="Log contact"
                              description={ACTION_HELP.contact}
                              icon={<PhoneIcon />}
                              onClick={() => { setContactMethod('call'); setContactTarget(item) }}
                            />
                          )}
                          <IconAction
                            label={item.acknowledged ? 'Un-acknowledge' : 'Acknowledge'}
                            description={item.acknowledged ? ACTION_HELP.unack : ACTION_HELP.ack}
                            icon={item.acknowledged ? <UndoIcon /> : <CheckCircleIcon />}
                            tone={item.acknowledged ? 'default' : 'primary'}
                            disabled={busyUsi === item.fcc_unique_system_identifier}
                            onClick={() => handleAck(item)}
                          />
                        </IconActions>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page} totalPages={totalPages} onPageChange={setPage}
              total={total} pageSize={pageSize}
              onPageSizeChange={(n) => { setPageSize(n); setPage(1) }}
            />
          </>
        )}
      </div>

      {confirming && (
        <Modal
          titleId="bulk-ack-confirm"
          title={
            confirming.acknowledged
              ? `Acknowledge ${total.toLocaleString()} alerts?`
              : `Un-acknowledge ${total.toLocaleString()} alerts?`
          }
          onClose={() => setConfirming(null)}
          maxWidth="max-w-md"
        >
          <p className="text-sm text-gray-600">
            This applies to all {total.toLocaleString()} obligations matching the current filters,
            including rows on pages you have not opened.
            {confirming.acknowledged
              ? ' Acknowledged alerts drop out of this list and the dashboard count — the deadlines themselves do not change.'
              : ' They will reappear in the default list and the dashboard count.'}
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setConfirming(null)} className="btn btn-secondary">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => runBulk(confirming.acknowledged)}
              disabled={busy}
              className="btn btn-primary disabled:opacity-50"
            >
              {busy
                ? 'Saving…'
                : confirming.acknowledged
                  ? `Acknowledge ${total.toLocaleString()}`
                  : `Un-acknowledge ${total.toLocaleString()}`}
            </button>
          </div>
        </Modal>
      )}

      {leadTarget && (
        <AddLeadModal
          usi={leadTarget.fcc_unique_system_identifier}
          subtitle={`${leadTarget.call_sign ?? 'License'} — ${leadTarget.entity_name || 'Unknown entity'}`}
          initialNote={
            `FCC construction notification ${leadTarget.status === 'overdue' ? 'was due' : 'due'} `
            + `${formatLocalDate(leadTarget.earliest_deadline)}.`
          }
          initialPriority={leadTarget.status === 'overdue' || leadTarget.days_remaining <= 60 ? 'high' : 'medium'}
          onClose={() => setLeadTarget(null)}
          onAdded={() => { setLeadTarget(null); load() }}
        />
      )}

      {messageTarget && (
        <SendMessageModal
          onClose={() => setMessageTarget(null)}
          clientId={messageTarget.client_id ?? undefined}
          leadId={messageTarget.client_id ? undefined : messageTarget.lead_id ?? undefined}
          fccUsi={
            messageTarget.client_id || messageTarget.lead_id
              ? undefined
              : messageTarget.fcc_unique_system_identifier
          }
          // A licensee hearing about this from us should get the plain heads-up,
          // never the internal 601 worksheet (see the plan for 2026-10-21).
          defaultTemplateKey="construction_courtesy_notice"
          constructionUsi={messageTarget.fcc_unique_system_identifier}
          onSent={() => { setMessageTarget(null); load() }}
        />
      )}

      {contactTarget && (
        <Modal
          titleId="log-contact-title"
          title="Log contact"
          subtitle={`${contactTarget.call_sign ?? 'License'} — ${contactTarget.entity_name || 'Unknown entity'}`}
          onClose={() => setContactTarget(null)}
          maxWidth="max-w-md"
        >
          <p className="text-sm text-gray-600 mb-3">
            Records that we've been in touch with this licensee about the deadline, so it stops
            showing as needing a first contact. A message sent from this page records itself.
          </p>
          <label htmlFor="log-contact-method" className="label text-sm">How</label>
          <select
            id="log-contact-method"
            className="input"
            value={contactMethod}
            onChange={e => setContactMethod(e.target.value as ContactMethod)}
          >
            {CONTACT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <ModalFooter>
            {contactTarget.contacted_at && (
              <button
                type="button"
                onClick={() => saveContact(contactTarget, false)}
                disabled={busyUsi !== null}
                className="btn btn-secondary mr-auto"
              >
                Clear contact
              </button>
            )}
            <button type="button" onClick={() => setContactTarget(null)} className="btn btn-secondary">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => saveContact(contactTarget, true)}
              disabled={busyUsi !== null}
              className="btn btn-primary"
            >
              Save
            </button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  )
}
